import React from 'react';

/**
 * AI Transparency Notice - Shown in AI-generated content areas
 */
export const AITransparencyNotice = () => (
  <div style={{
    marginTop: '16px',
    padding: '12px',
    background: 'rgba(158, 158, 158, 0.08)',
    borderRadius: '8px',
    borderLeft: '3px solid #9e9e9e'
  }}>
    <p style={{
      margin: 0,
      fontSize: '12px',
      color: '#666',
      lineHeight: '1.5',
      fontStyle: 'italic'
    }}>
      ⓘ <strong>AI Transparency:</strong> These insights are generated using artificial intelligence based on the information you provide and available data sources. Results may be inaccurate or incomplete and should be used as guidance, not certainty.
    </p>
  </div>
);

/**
 * Nutrition Data Disclaimer - Shown with nutrition estimates
 */
export const NutritionDataDisclaimer = () => (
  <div style={{
    marginTop: '10px',
    padding: '8px 10px',
    background: 'rgba(158, 158, 158, 0.05)',
    borderRadius: '6px',
    borderLeft: '2px solid #bbb'
  }}>
    <p style={{
      margin: 0,
      fontSize: '11px',
      color: '#888',
      lineHeight: '1.4',
      fontStyle: 'italic'
    }}>
      ℹ️ Nutritional values are estimates based on public databases, AI interpretation, and user inputs. Actual values may vary by brand, preparation, and portion size.
    </p>
  </div>
);

/**
 * PremiumLockCard - Reusable component for showing premium locks
 * Variants: "default" (full card), "inline", "compact"
 */
export const PremiumLockCard = ({ 
  title = "🔒 Unlock why progress stalls",
  body = "See what's holding you back and exactly how to fix it for your body.",
  buttonText = "Unlock Now",
  onUpgrade = null,
  variant = "default", // "default", "inline", "compact"
  children = null // Optional: content to show above the lock
}) => {
  
  if (variant === "compact") {
    return (
      <div style={{
        padding: '12px 16px',
        background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(118, 75, 162, 0.08) 100%)',
        borderRadius: '10px',
        border: '1px dashed rgba(102, 126, 234, 0.3)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '18px' }}>🔒</span>
          <div>
            <p style={{ margin: 0, fontSize: '13px', fontWeight: '500', color: 'var(--text-primary)' }}>{title.replace('🔒 ', '')}</p>
            <p style={{ margin: '2px 0 0 0', fontSize: '11px', color: 'var(--text-secondary)' }}>{body}</p>
          </div>
        </div>
        <button
          onClick={onUpgrade}
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            border: 'none',
            padding: '8px 14px',
            borderRadius: '8px',
            fontSize: '11px',
            fontWeight: '600',
            cursor: 'pointer',
            whiteSpace: 'nowrap'
          }}
        >
          Unlock
        </button>
      </div>
    );
  }
  
  if (variant === "inline") {
    return (
      <div style={{
        padding: '16px',
        background: 'rgba(0,0,0,0.03)',
        borderRadius: '12px',
        border: '1px dashed rgba(0,0,0,0.1)'
      }}>
        {children}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '12px',
          marginTop: children ? '12px' : '0'
        }}>
          <span style={{ fontSize: '24px' }}>🔒</span>
          <div style={{ flex: 1 }}>
            <p style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)' }}>{title}</p>
            <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: 'var(--text-secondary)' }}>{body}</p>
          </div>
          <button
            onClick={onUpgrade}
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              padding: '10px 16px',
              borderRadius: '8px',
              fontSize: '12px',
              fontWeight: '600',
              cursor: 'pointer',
              whiteSpace: 'nowrap'
            }}
          >
            {buttonText}
          </button>
        </div>
      </div>
    );
  }
  
  // Default variant - full card
  return (
    <div style={{
      padding: '24px',
      background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
      borderRadius: '16px',
      border: '1px dashed rgba(102, 126, 234, 0.3)',
      textAlign: 'center'
    }}>
      {children}
      <div style={{ 
        fontSize: '40px', 
        marginBottom: '12px',
        marginTop: children ? '16px' : '0'
      }}>🔒</div>
      <h4 style={{ 
        margin: '0 0 8px 0', 
        fontSize: '16px', 
        fontWeight: '600',
        color: 'var(--text-primary)'
      }}>{title}</h4>
      <p style={{ 
        margin: '0 0 16px 0', 
        fontSize: '13px', 
        color: 'var(--text-secondary)',
        lineHeight: '1.5'
      }}>{body}</p>
      <button
        onClick={onUpgrade}
        style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          border: 'none',
          padding: '12px 24px',
          borderRadius: '10px',
          fontSize: '14px',
          fontWeight: '600',
          cursor: 'pointer'
        }}
      >
        {buttonText}
      </button>
    </div>
  );
};

export default {
  AITransparencyNotice,
  NutritionDataDisclaimer,
  PremiumLockCard
};
